import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../../assets/img/logo.png';
import logoDark from '../../../assets/img/logo_dark.png';
import { ROUTES } from '../../routes/paths';
import color from '../../../theme/Color';
import { Features } from '../../../assets/features/Features';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Use language from context
  const { language, toggleLanguage } = useLanguage();
  // Use theme from context
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Check if current path is a service page or clinic page
  const isServicePage = currentPath.startsWith('/services/');
  const isClinicPage = currentPath.startsWith('/clinic/');
  const isHomePage = currentPath === ROUTES.HOME;
  const isAboutPage = currentPath === ROUTES.ABOUT_US;
  const isGalleryPage = currentPath === ROUTES.GALLERY;
  const isServicesPage = currentPath === ROUTES.SERVICES;
  const isLocationPage = currentPath === ROUTES.LOCATION;
  const isArbitrationPage = currentPath === ROUTES.ARBITRATION;
  const isTermosPage = currentPath === ROUTES.TERMOS;
  const isContactPage = currentPath === ROUTES.CONTACT;

  // Close mobile nav function
  const closeMobileNav = () => {
    document.body.classList.remove('mobile-nav-active');
    const mobileNavToggle = document.querySelector('.mobile-nav-toggle') as HTMLElement;
    if (mobileNavToggle) {
      mobileNavToggle.classList.remove('bi-x');
      mobileNavToggle.classList.add('bi-list');
    }
  };
  
  // Toggle mobile nav function
  const toggleMobileNav = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const isActive = document.body.classList.contains('mobile-nav-active');
    if (isActive) {
      document.body.classList.remove('mobile-nav-active');
      e.currentTarget.classList.remove('bi-x');
      e.currentTarget.classList.add('bi-list');
    } else {
      document.body.classList.add('mobile-nav-active');
      e.currentTarget.classList.remove('bi-list');
      e.currentTarget.classList.add('bi-x');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Auto-close mobile nav when route changes
    closeMobileNav();
  }, [location.pathname]);

  // Handle overlay click to close mobile nav
  useEffect(() => {
    const handleOverlayClick = (e: MouseEvent) => {
      if (!document.body.classList.contains('mobile-nav-active')) return;
      
      const target = e.target as HTMLElement;
      const navMenu = document.querySelector('#navmenu');
      const mobileNavToggle = document.querySelector('.mobile-nav-toggle');
      const mobileNavClose = document.querySelector('.mobile-nav-close');
      
      // Close if click is outside nav menu and not on toggle/close buttons
      if (navMenu && 
          !navMenu.contains(target) && 
          target !== navMenu &&
          !mobileNavToggle?.contains(target) &&
          !mobileNavClose?.contains(target)) {
        closeMobileNav();
      }
    };

    document.addEventListener('click', handleOverlayClick);
    return () => {
      document.removeEventListener('click', handleOverlayClick);
    };
  }, []);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    let isHidden = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
      const topbar = document.querySelector('.topbar');
      if (topbar) {
            const currentScrollY = window.scrollY;
            const scrollDelta = currentScrollY - lastScrollY;
            const isScrollingDown = scrollDelta > 0;
            const isScrollingUp = scrollDelta < 0;
            const isAtTop = currentScrollY <= 10;
            
            // Only toggle if there's a meaningful scroll change (more than 5px)
            if (Math.abs(scrollDelta) > 5) {
              // Hide topbar when scrolling down past threshold
              if (isScrollingDown && currentScrollY > 20 && !isHidden) {
          topbar.classList.add('topbar-hidden');
                isHidden = true;
              } 
              // Show topbar when scrolling up or at top
              else if ((isScrollingUp || isAtTop) && isHidden) {
                topbar.classList.remove('topbar-hidden');
                isHidden = false;
              }
            }
            
            // Always show at top
            if (isAtTop && isHidden) {
          topbar.classList.remove('topbar-hidden');
              isHidden = false;
            }
            
            lastScrollY = currentScrollY;
        }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle scroll to top for home page link
  const handleHomeClick = (e: React.MouseEvent) => {
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    closeMobileNav();
  };

  // Handle appointment button click
  const handleAppointmentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (isHomePage) {
      // If on home page, scroll to top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If not on home page, navigate to home and then scroll to top
      navigate(ROUTES.HOME);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  // Handle contact link click
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Always navigate to the contact page since there's no contact section in HomePage
    navigate(ROUTES.CONTACT);
    closeMobileNav();
  };
  
  // Handle navigation link click (for auto-closing mobile nav)
  const handleNavLinkClick = () => {
    closeMobileNav();
  };

  // Prevent default action for clinic dropdown
  const handleClinicClick = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // Style for active items
  const activeStyle = {
    backgroundColor: color.blue.main,
    borderRadius: '4px',
    color: '#fff',
    padding: '5px 15px',
    margin: '0 10px',
    display: 'inline-block',
    width: 'calc(100% - 20px)',
  };

  // Style for dropdown links in dark mode
  const dropdownLinkStyle = isDarkMode ? {
    color: '#f3f4f6'
  } : {};

  // Language switcher button style
  const languageSwitcherStyle = {
    marginLeft: '10px',
    padding: '8px 15px',
    borderRadius: '50px',
    border: '2px solid #1977cc',
    color: '#1977cc',
    background: 'transparent',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  // WhatsApp message text based on language
  const whatsappMessage = language === 'en' 
    ? 'I would like to be contacted' 
    : 'Gostaria de ser contactado';

  // WhatsApp link with phone number
  const whatsappLink = `https://api.whatsapp.com/send?phone=+351927202009&text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <header id="header" className="header sticky-top">
      <div className="topbar d-flex align-items-center">
        <div className="container d-flex justify-content-center justify-content-md-between">
          <div className="contact-info d-flex align-items-center">
            <i className="bi bi-envelope d-flex align-items-center">
              <a href="mailto:info@clinicaosteopatia.pt">{Features.header.contact.email[language]}</a>
            </i>
            <i className="bi bi-phone d-flex align-items-center ms-4">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="whatsapp-link"
              >
                {Features.header.contact.phone[language]}
              </a>
              <span
                className="phone-info-icon"
                data-tooltip-text={language === 'en' ? 'Call to a national landline' : 'Chamada para a rede fixa nacional'}
              >
                <i className="bi bi-info-circle-fill"></i>
              </span>
            </i>
          </div>
          <div className="social-links d-none d-md-flex align-items-center">
            <a href="https://www.instagram.com/guiosteopata/" target="_blank" rel="noreferrer" className="instagram"><i className="bi bi-instagram"></i></a>
            <a href="https://www.facebook.com/clinicaosteopatiapt" target="_blank" rel="noreferrer" className="facebook"><i className="bi bi-facebook"></i></a>
          </div>
        </div>
      </div>

      <div className="branding d-flex align-items-center" style={{ padding: '10px 0' }}>
        <div className="container position-relative d-flex align-items-center justify-content-between">
          <Link 
            to="/" 
            className="logo d-flex align-items-center me-auto" 
            style={{ height: '55px' }}
            onClick={handleHomeClick}
          >
            <img 
              src={isDarkMode ? logoDark : logo} 
              alt="Logo" 
              style={{ 
                maxHeight: '100%',
                width: 'auto',
                objectFit: 'contain'
              }} 
            />
          </Link>

          <nav id="navmenu" className="navmenu">
            {/* Mobile Nav Logo */}
            <div className="mobile-nav-logo d-xl-none">
              <Link to="/" onClick={closeMobileNav}>
                <img 
                  src={isDarkMode ? logoDark : logo} 
                  alt="Clínica Dr. Guilherme Galvão" 
                  style={{ 
                    maxHeight: '45px',
                    width: 'auto',
                    objectFit: 'contain'
                  }} 
                />
              </Link>
            </div>
            
            <ul>
              <li>
                <Link 
                  to="/" 
                  className={isHomePage ? "active" : ""} 
                  onClick={handleHomeClick}
                >
                  {Features.header.navigation.home[language]}
                </Link>
              </li>
              <li className="dropdown">
                <a href="#" onClick={handleClinicClick} className={isClinicPage ? "active" : ""}>
                  <span>{Features.header.navigation.clinic.title[language]}</span> <i className="bi bi-chevron-down toggle-dropdown"></i>
                </a>
                <ul className="animated-dropdown">
                  <li>
                    <Link 
                      to={ROUTES.ABOUT_US} 
                      style={currentPath === ROUTES.ABOUT_US ? activeStyle : dropdownLinkStyle}
                      className="dropdown-item-animated"
                      onClick={handleNavLinkClick}
                    >
                      {Features.header.navigation.clinic.aboutUs[language]}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.GALLERY} 
                      style={currentPath === ROUTES.GALLERY ? activeStyle : dropdownLinkStyle}
                      className="dropdown-item-animated"
                      onClick={handleNavLinkClick}
                    >
                      {Features.header.navigation.clinic.gallery[language]}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.LOCATION} 
                      style={currentPath === ROUTES.LOCATION ? activeStyle : dropdownLinkStyle}
                      className="dropdown-item-animated"
                      onClick={handleNavLinkClick}
                    >
                      {Features.header.navigation.clinic.location[language]}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to={ROUTES.ARBITRATION} 
                      style={currentPath === ROUTES.ARBITRATION ? activeStyle : dropdownLinkStyle}
                      className="dropdown-item-animated"
                      onClick={handleNavLinkClick}
                    >
                      {Features.header.navigation.clinic.arbitration[language]}
                    </Link>
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href={ROUTES.CONTACT}
                  className={isContactPage ? "active" : ""} 
                  onClick={handleContactClick}
                >
                  {Features.header.navigation.contact[language]}
                </a>
              </li>
            </ul>
          </nav>

          <div className="d-flex align-items-center">
            <a 
              href="#appointment" 
              className="cta-btn d-none d-sm-block"
              onClick={handleAppointmentClick}
            >
              {Features.header.appointmentButton[language]}
            </a>
            <button
              onClick={toggleTheme}
              className="theme-toggle-btn d-none d-sm-block mx-2"
              aria-label="Toggle theme"
              title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              <i className={`bi ${theme === 'light' ? 'bi-moon' : 'bi-sun'}`}></i>
            </button>
            <button
              onClick={toggleLanguage}
              style={languageSwitcherStyle}
              className="d-none d-sm-block"
              aria-label="Toggle language"
              title={language === 'pt' ? 'Switch to English' : 'Mudar para Português'}
            >
              {language === 'pt' ? 'EN' : 'PT'}
            </button>
            <button 
              className="mobile-nav-toggle bi bi-list d-xl-none"
              onClick={toggleMobileNav}
              aria-label="Toggle menu"
              type="button"
              data-react-handler="true"
              style={{ zIndex: 9999, position: 'relative' }}
            ></button>
          </div>
        </div>
      </div>
      
      {/* Add some custom styles for the WhatsApp link */}
      <style>
        {`
          .whatsapp-link {
            color: inherit;
            text-decoration: none;
            transition: color 0.3s ease;
          }
          .whatsapp-link:hover {
            color: #1977cc;
          }
          
          /* Mobile nav controls */
          .mobile-nav-controls {
            display: flex;
            align-items: center;
          }
          
          .mobile-lang-toggle {
            background-color: #1977cc;
            color: white;
            border: none;
            border-radius: 4px;
            padding: 4px 8px;
            font-size: 14px;
            cursor: pointer;
            transition: background-color 0.3s ease;
          }
          
          .mobile-lang-toggle:hover {
            background-color: #166ab9;
          }
          
          .mobile-theme-toggle {
            background-color: #1977cc;
            color: white;
            border: none;
            border-radius: 4px;
            padding: 4px 8px;
            font-size: 14px;
            cursor: pointer;
            transition: background-color 0.3s ease;
          }
          
          .mobile-theme-toggle:hover {
            background-color: #166ab9;
          }
          
          .theme-toggle-btn {
            background-color: transparent;
            color: #1977cc;
            border: 2px solid #1977cc;
            border-radius: 50%;
            width: 38px;
            height: 38px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
          }
          
          .theme-toggle-btn:hover {
            background-color: #1977cc;
            color: white;
          }

          /* Dropdown animation styles */
          .animated-dropdown {
            animation: fadeInDown 0.3s ease-in-out;
          }

          .dropdown-item-animated {
            position: relative;
            transition: all 0.3s ease;
            padding: 10px 15px;
            display: block;
            width: 100%;
            border-radius: 4px;
            overflow: hidden;
          }

          .dropdown-item-animated::before {
            content: '';
            position: absolute;
            left: 0;
            bottom: 0;
            height: 2px;
            width: 0;
            background-color: #1977cc;
            transition: width 0.3s ease;
          }

          .dropdown-item-animated:hover {
            transform: translateX(3px);
            background-color: rgba(25, 119, 204, 0.05);
          }

          .dropdown-item-animated:hover::before {
            width: 100%;
          }

          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Dark mode dropdown styles */
          .dark-theme .dropdown ul {
            background-color: #2a2c2e !important;
            border-color: #3a3b3c !important;
          }
          
          .dark-theme .dropdown ul a {
            color: #f3f4f6 !important;
          }
          
          .dark-theme .dropdown ul a:hover {
            color: #ffffff !important;
            background-color: rgba(50, 145, 230, 0.1) !important;
          }

          .dark-theme .dropdown-item-animated:hover::before {
            background-color: #3291e6;
          }
          
          @media (max-width: 767px) {
            .mobile-nav-controls {
              position: absolute;
              right: 15px;
              top: 50%;
              transform: translateY(-50%);
            }
          }
          
          /* Mobile Nav Toggle Button */
          .mobile-nav-toggle {
            background: transparent;
            border: none;
            color: var(--nav-color, #2c4964);
            font-size: 28px;
            line-height: 0;
            cursor: pointer;
            padding: 8px;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .mobile-nav-toggle:hover {
            color: var(--nav-hover-color, #1977cc);
            transform: scale(1.1);
          }
          
          .mobile-nav-active .mobile-nav-toggle {
            color: #1977cc;
            position: fixed;
            font-size: 32px;
            top: 15px;
            left: 340px;
            z-index: 10001;
            background: rgba(255, 255, 255, 0.95);
            border-radius: 50%;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
            transition: all 0.3s ease;
          }
          
          .dark-theme .mobile-nav-active .mobile-nav-toggle {
            background: rgba(42, 44, 46, 0.95);
            color: #fff;
          }
          
          .mobile-nav-active .mobile-nav-toggle:hover {
            transform: scale(1.1);
            background: rgba(255, 255, 255, 1);
          }
          
          .dark-theme .mobile-nav-active .mobile-nav-toggle:hover {
            background: rgba(42, 44, 46, 1);
          }
          
          @media (max-width: 400px) {
            .mobile-nav-active .mobile-nav-toggle {
              left: calc(85vw + 10px);
            }
          }
          
          /* Mobile Nav Close Button */
          .mobile-nav-close {
            display: none;
          }
          
          /* Beautiful Mobile Nav - Slides from Left */
          @media (max-width: 1199px) {
            /* Initial state - navmenu hidden off-screen */
            #navmenu.navmenu,
            header #navmenu,
            .navmenu {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              width: 320px !important;
              max-width: 85vw !important;
              height: 100vh !important;
              transform: translateX(-100%) !important;
              transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
              z-index: 10000 !important;
              visibility: hidden !important;
              opacity: 0 !important;
            }
            
            /* Close button when nav is active */
            body.mobile-nav-active .mobile-nav-close {
              display: flex !important;
              position: absolute;
              top: 20px;
              right: 20px;
              z-index: 10001;
              background: rgba(255, 255, 255, 0.9);
              border: 2px solid rgba(25, 119, 204, 0.2);
              color: #1977cc;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              cursor: pointer;
              transition: all 0.3s ease;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            body.mobile-nav-active .dark-theme .mobile-nav-close,
            body.dark-theme.mobile-nav-active .mobile-nav-close {
              background: rgba(42, 44, 46, 0.9);
              border-color: rgba(50, 145, 230, 0.3);
              color: #3291e6;
            }
            
            body.mobile-nav-active .mobile-nav-close:hover {
              background: rgba(255, 255, 255, 1);
              border-color: #1977cc;
              transform: rotate(90deg) scale(1.1);
            }
            
            body.mobile-nav-active .dark-theme .mobile-nav-close:hover,
            body.dark-theme.mobile-nav-active .mobile-nav-close:hover {
              background: rgba(42, 44, 46, 1);
              border-color: #3291e6;
            }
            
            /* Override main.css default styles - must be very specific */
            body.mobile-nav-active #navmenu.navmenu,
            body.mobile-nav-active header #navmenu,
            body.mobile-nav-active .navmenu {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: auto !important;
              bottom: auto !important;
              width: 320px !important;
              max-width: 85vw !important;
              height: 100vh !important;
              min-height: 100vh !important;
              background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%) !important;
              box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2) !important;
              z-index: 10000 !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
              display: flex !important;
              flex-direction: column !important;
              margin: 0 !important;
              padding: 0 !important;
              visibility: visible !important;
              opacity: 1 !important;
              transform: translateX(0) !important;
              transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
              border-radius: 0 !important;
              backdrop-filter: none !important;
              filter: none !important;
            }
            
            /* Override inset shorthand from main.css - use individual properties */
            body.mobile-nav-active #navmenu.navmenu {
              top: 0 !important;
              left: 0 !important;
              right: auto !important;
              bottom: auto !important;
            }
            
            body.mobile-nav-active .dark-theme #navmenu,
            body.mobile-nav-active .dark-theme .navmenu,
            body.dark-theme.mobile-nav-active #navmenu,
            body.dark-theme.mobile-nav-active .navmenu {
              background: linear-gradient(180deg, #1a1b1e 0%, #242526 100%) !important;
              box-shadow: 4px 0 24px rgba(0, 0, 0, 0.6) !important;
            }
            
            @keyframes slideInFromLeft {
              from {
                transform: translateX(-100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            
            /* Mobile Nav Logo */
            .mobile-nav-logo {
              display: none;
            }
            
            body.mobile-nav-active .mobile-nav-logo {
              display: block;
              padding: 25px 20px 20px 20px;
              border-bottom: 2px solid rgba(25, 119, 204, 0.1);
              background: linear-gradient(135deg, rgba(25, 119, 204, 0.05) 0%, transparent 100%);
              text-align: left;
              animation: fadeInDown 0.5s ease-out 0.2s both;
            }
            
            body.mobile-nav-active .dark-theme .mobile-nav-logo,
            body.dark-theme.mobile-nav-active .mobile-nav-logo {
              border-bottom-color: rgba(255, 255, 255, 0.1);
              background: linear-gradient(135deg, rgba(50, 145, 230, 0.1) 0%, transparent 100%);
            }
            
            @keyframes fadeInDown {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .mobile-nav-logo img {
              transition: transform 0.3s ease;
              filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
              max-height: 45px !important;
              width: auto;
              object-fit: contain;
            }
            
            .dark-theme .mobile-nav-logo img {
              filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
            }
            
            .mobile-nav-logo:hover img {
              transform: scale(1.05);
            }
            
            /* Mobile Nav Overlay - removed dark effect */
            body.mobile-nav-active::before {
              display: none;
            }
            
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            
            /* Mobile Nav Menu List - override main.css display: none */
            body.mobile-nav-active #navmenu ul,
            body.mobile-nav-active .navmenu ul {
              display: flex !important;
              flex-direction: column !important;
              list-style: none !important;
              padding: 20px 0 !important;
              margin: 0 !important;
              flex: 1 !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
              position: static !important;
              inset: auto !important;
              border-radius: 0 !important;
              background-color: transparent !important;
              border: none !important;
              box-shadow: none !important;
            }
            
            /* Mobile Nav Links */
            body.mobile-nav-active .navmenu > ul > li {
              border-bottom: 1px solid rgba(0, 0, 0, 0.05);
              animation: slideInRight 0.4s ease-out forwards;
              opacity: 0;
            }
            
            body.mobile-nav-active .dark-theme .navmenu > ul > li,
            body.dark-theme.mobile-nav-active .navmenu > ul > li {
              border-bottom-color: rgba(255, 255, 255, 0.1);
            }
            
            @keyframes slideInRight {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            
            body.mobile-nav-active .navmenu > ul > li:nth-child(1) { animation-delay: 0.1s; }
            body.mobile-nav-active .navmenu > ul > li:nth-child(2) { animation-delay: 0.15s; }
            body.mobile-nav-active .navmenu > ul > li:nth-child(3) { animation-delay: 0.2s; }
            body.mobile-nav-active .navmenu > ul > li:nth-child(4) { animation-delay: 0.25s; }
            body.mobile-nav-active .navmenu > ul > li:nth-child(5) { animation-delay: 0.3s; }
            body.mobile-nav-active .navmenu > ul > li:nth-child(6) { animation-delay: 0.35s; }
            
            body.mobile-nav-active .navmenu a {
              padding: 16px 24px;
              font-size: 16px;
              font-weight: 500;
              color: #2c4964;
              display: flex;
              align-items: center;
              justify-content: space-between;
              text-decoration: none;
              transition: all 0.3s ease;
              position: relative;
            }
            
            body.mobile-nav-active .dark-theme .navmenu a,
            body.dark-theme.mobile-nav-active .navmenu a {
              color: #f3f4f6;
            }
            
            body.mobile-nav-active .navmenu a:hover,
            body.mobile-nav-active .navmenu a.active {
              background: linear-gradient(90deg, rgba(25, 119, 204, 0.1) 0%, transparent 100%);
              color: #1977cc;
              padding-left: 28px;
            }
            
            body.mobile-nav-active .dark-theme .navmenu a:hover,
            body.dark-theme.mobile-nav-active .navmenu a:hover,
            body.mobile-nav-active .dark-theme .navmenu a.active,
            body.dark-theme.mobile-nav-active .navmenu a.active {
              background: linear-gradient(90deg, rgba(50, 145, 230, 0.2) 0%, transparent 100%);
              color: #3291e6;
            }
            
            body.mobile-nav-active .navmenu a::before {
              content: '';
              position: absolute;
              left: 0;
              top: 0;
              bottom: 0;
              width: 4px;
              background: #1977cc;
              transform: scaleY(0);
              transition: transform 0.3s ease;
            }
            
            body.mobile-nav-active .dark-theme .navmenu a::before,
            body.dark-theme.mobile-nav-active .navmenu a::before {
              background: #3291e6;
            }
            
            body.mobile-nav-active .navmenu a:hover::before,
            body.mobile-nav-active .navmenu a.active::before {
              transform: scaleY(1);
            }
            
            /* Dropdown Styles */
            body.mobile-nav-active .navmenu .dropdown ul {
              display: none;
              background: rgba(0, 0, 0, 0.02);
              margin: 0;
              padding: 0;
            }
            
            body.mobile-nav-active .dark-theme .navmenu .dropdown ul,
            body.dark-theme.mobile-nav-active .navmenu .dropdown ul {
              background: rgba(255, 255, 255, 0.05);
            }
            
            body.mobile-nav-active .navmenu .dropdown > .dropdown-active {
              display: block;
            }
            
            body.mobile-nav-active .navmenu .dropdown ul a {
              padding: 12px 24px 12px 48px;
              font-size: 15px;
              font-weight: 400;
            }
            
            body.mobile-nav-active .navmenu .dropdown ul a:hover {
              padding-left: 52px;
              background: rgba(25, 119, 204, 0.08);
            }
            
            body.mobile-nav-active .dark-theme .navmenu .dropdown ul a:hover,
            body.dark-theme.mobile-nav-active .navmenu .dropdown ul a:hover {
              background: rgba(50, 145, 230, 0.15);
            }
            
            /* Dropdown Toggle Icon */
            body.mobile-nav-active .navmenu .dropdown > a i {
              transition: transform 0.3s ease;
            }
            
            body.mobile-nav-active .navmenu .dropdown > .dropdown-active > a i {
              transform: rotate(180deg);
            }
          }
        `}
      </style>
    </header>
  );
};

export default Header; 